
import React, { useEffect, useState } from "react";
import { getFirestore, collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { getAuth, reauthenticateWithCredential, EmailAuthProvider, updatePassword, signOut } from "firebase/auth";
import { app } from "../../firebase";
import { User, Mail, Phone, Loader2, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../store/authSlice";

const PRIMARY = "#0071bd";
const SECONDARY = "#f3f4f6";

const AdvancedProfilePage = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ fullName: "", mobile: "" });
  const [saveLoading, setSaveLoading] = useState(false);
  const db = getFirestore(app);
  const [showToast, setShowToast] = useState(false);
  const [showError, setShowError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [confirmDirty, setConfirmDirty] = useState(false);
  const [pwStrengthDirty, setPwStrengthDirty] = useState(false);

  // Password strength regex: min 8, 1 upper, 1 lower, 1 special
  const pwStrengthRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
  const pwStrengthValid = pwStrengthRegex.test(pwForm.newPassword);
  const navigate = useNavigate();
   const dispatch = useDispatch();
  // Password change logic


  const handleLogout = () => {
      dispatch(logout());
    };

  const handlePwChange = async (e) => {
    e.preventDefault();
    setShowError("");
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setShowError("New passwords do not match.");
      return;
    }
    setPwLoading(true);
    try {
      const auth = getAuth(app);
      const currentUser = auth.currentUser;
      if (!currentUser || !user?.emailId) throw new Error("User not authenticated");
      // Re-authenticate
      const credential = EmailAuthProvider.credential(user.emailId, pwForm.oldPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, pwForm.newPassword);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
      setTimeout(async () => {
        handleLogout();
        navigate("/login");
      }, 1000);
      setPwForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      // User-friendly error mapping
      let msg = err.message || "Failed to change password";
      if (msg.includes("auth/invalid-credential")) msg = "Old password is incorrect.";
      else if (msg.includes("auth/weak-password")) msg = "New password is too weak (min 6 characters).";
      else if (msg.includes("auth/too-many-requests")) msg = "Too many attempts. Please try again later.";
      else if (msg.includes("auth/network-request-failed")) msg = "Network error. Please check your connection.";
      else if (msg.includes("auth/requires-recent-login")) msg = "Please log in again and try changing your password.";
      setShowError(msg);
    } finally {
      setPwLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.emailId) return;
    const fetchProfile = async () => {
      setLoading(true);
      const q = query(collection(db, "BDUsers"), where("emailId", "==", user.emailId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = snap.docs[0].data();
        setProfile({ ...data, id: snap.docs[0].id });
        setForm({ fullName: data?.fullName || "", mobile: data?.mobile  ?? data?.contact ?? "" });
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user]);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!profile) return;
    setSaveLoading(true);
    await updateDoc(doc(db, "BDUsers", profile.id), {
      fullName: form.fullName,
      mobile: form.mobile,
    });
    setProfile(p => ({ ...p, fullName: form.fullName, mobile: form.mobile }));
    setEdit(false);
    setSaveLoading(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-[#0071bd] mb-2" size={32} />
        <span className="text-[#0071bd] font-semibold">Loading profile...</span>
      </div>
    );
  }

  if (!profile) {
    return <div className="text-center text-gray-500 mt-12">Profile not found.</div>;
  }

  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl p-8 mt-12 border border-gray-100">
      {showToast && (
        <div className="mb-4 px-4 py-2 rounded-lg bg-[#0071bd] text-white text-center font-semibold shadow animate-fade-in">
          Profile updated successfully!
        </div>
      )}
      {/* Responsive profile header for world-class mobile/desktop UI */}
      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mb-6">
        {/* Icon/Avatar on top in mobile, left in desktop */}
        <div className="order-1 sm:order-none flex flex-col items-center">
          <div className="bg-[#0071bd] text-white rounded-full w-20 h-20 flex items-center justify-center text-4xl font-bold shadow-lg border-4 border-white mb-2 sm:mb-0">
            {profile.fullName?.[0] || profile.emailId?.[0] || "U"}
          </div>
        </div>
        {/* Name and email below icon in mobile, right in desktop */}
        <div className="order-2 sm:order-none flex flex-col items-center sm:items-start w-full">
          <h2 className="text-xl sm:text-2xl font-bold text-[#0071bd] flex items-center gap-2 text-center sm:text-left">
            <User size={22} className="hidden sm:inline-block text-[#0071bd]" />
            {profile.fullName || "-"}
          </h2>
          <div className="text-gray-500 flex items-center gap-2 mt-1 text-center sm:text-left">
            <Mail size={16} className="hidden sm:inline-block" />
            {profile.emailId}
          </div>
        </div>
      </div>
      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Full Name</label>
          <input
            type="text"
            name="fullName"
            value={form.fullName}
            onChange={handleChange}
            disabled={!edit}
            className={`w-full px-4 py-2 rounded-lg border ${edit ? 'border-[#0071bd]' : 'border-gray-200'} bg-${edit ? 'white' : 'gray-100'} text-lg focus:outline-none focus:ring-2 focus:ring-[#0071bd]`}
            style={{ background: edit ? '#fff' : SECONDARY }}
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Mobile Number</label>
          <input
            type="tel"
            name="mobile"
            value={form?.mobile}
            onChange={handleChange}
            disabled={!edit}
            className={`w-full px-4 py-2 rounded-lg border ${edit ? 'border-[#0071bd]' : 'border-gray-200'} bg-${edit ? 'white' : 'gray-100'} text-lg focus:outline-none focus:ring-2 focus:ring-[#0071bd]`}
            style={{ background: edit ? '#fff' : SECONDARY }}
            required
          />
        </div>
        <div className="flex gap-4 mt-4">
          {!edit ? (
            <button
              type="button"
              className="flex-1 py-2 bg-[#0071bd] text-white rounded-lg font-semibold hover:bg-[#005fa3] transition"
              onClick={() => setEdit(true)}
            >
              Edit Profile
            </button>
          ) : (
            <>
              <button
                type="button"
                className="flex-1 py-2 bg-[#0071bd] text-white rounded-lg font-semibold hover:bg-[#005fa3] transition"
                disabled={saveLoading}
                onClick={handleSave}
              >
                {saveLoading ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                onClick={() => { setEdit(false); setForm({ fullName: profile.fullName, mobile: profile?.mobile }); }}
                disabled={saveLoading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {/* Change Password Section */}
      <div className="mt-12 border-t pt-8">
        <h3 className="text-xl font-bold text-[#0071bd] mb-4 text-center">Change Password</h3>
        <form className="max-w-md mx-auto space-y-5 flex flex-col items-center" onSubmit={handlePwChange}>
          <div className="w-full">
            <label className="block text-gray-700 font-semibold mb-1">Old Password</label>
            <input
              type="password"
              name="oldPassword"
              value={pwForm.oldPassword}
              onChange={e => setPwForm(f => ({ ...f, oldPassword: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-100 text-lg focus:outline-none focus:ring-2 focus:ring-[#0071bd]"
              placeholder="Enter old password"
              required
            />
          </div>
          <div className="w-full relative">
            <label className="block text-gray-700 font-semibold mb-1 flex items-center gap-1">
              New Password
              <span className="relative group cursor-pointer">
                <Info size={16} className="text-yellow-600" />
                <span className="absolute left-6 top-1 z-10 hidden group-hover:block bg-yellow-100 border border-yellow-300 text-yellow-900 text-xs rounded px-3 py-2 shadow-lg min-w-[220px] max-w-xs whitespace-pre-line">
                  Password must be at least 8 characters, include:
                  {'\n'}• 1 uppercase letter
                  {'\n'}• 1 lowercase letter
                  {'\n'}• 1 special character
                </span>
              </span>
            </label>
            <input
              type="password"
              name="newPassword"
              value={pwForm.newPassword}
              onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))}
              onBlur={() => setPwStrengthDirty(true)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-100 text-lg focus:outline-none focus:ring-2 focus:ring-[#0071bd]"
              placeholder="Enter new password"
              required
            />
          </div>
          <div className="w-full">
           <label className="block text-gray-700 font-semibold mb-1 flex items-center gap-1">
              Confirm Password
              <span className="relative group cursor-pointer">
                <Info size={16} className="text-yellow-600" />
                <span className="absolute left-6 top-1 z-10 hidden group-hover:block bg-yellow-100 border border-yellow-300 text-yellow-900 text-xs rounded px-3 py-2 shadow-lg min-w-[220px] max-w-xs whitespace-pre-line">
                  Password must be at least 8 characters, include:
                  {'\n'}• 1 uppercase letter
                  {'\n'}• 1 lowercase letter
                  {'\n'}• 1 special character
                </span>
              </span>
            </label>
            
            <input
              type="password"
              name="confirmPassword"
              value={pwForm.confirmPassword}
              onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))}
              onBlur={() => setConfirmDirty(true)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-100 text-lg focus:outline-none focus:ring-2 focus:ring-[#0071bd]"
              placeholder="Confirm new password"
              required
            />
          </div>
          {showError && (
            <div className="w-full text-red-600 text-sm text-center font-semibold mb-2">{showError}</div>
          )}
          {pwStrengthDirty && pwForm.newPassword && !pwStrengthValid && (
            <div className="w-full text-red-500 text-xs text-center font-semibold mb-2">
              Password must be at least 8 characters, include 1 uppercase, 1 lowercase, and 1 special character.
            </div>
          )}
          {confirmDirty && pwForm.newPassword && pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword && (
            <div className="w-full text-red-500 text-xs text-center font-semibold mb-2">Passwords do not match.</div>
          )}
          <button
            type="submit"
            className={`w-full py-2 rounded-lg font-semibold mt-2 transition ${
              pwLoading ||
              !pwForm.oldPassword ||
              !pwForm.newPassword ||
              !pwForm.confirmPassword ||
              pwForm.newPassword !== pwForm.confirmPassword
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-[#0071bd] text-white hover:bg-[#005fa3]'
            }`}
            disabled={
              pwLoading ||
              !pwForm.oldPassword ||
              !pwForm.newPassword ||
              !pwForm.confirmPassword ||
              pwForm.newPassword !== pwForm.confirmPassword ||
              !pwStrengthValid
            }
          >
            {pwLoading ? "Changing..." : "Change Password"}
          </button>
        </form>
        {showToast && (
          <div className="mt-4 px-4 py-2 rounded-lg bg-[#0071bd] text-white text-center font-semibold shadow animate-fade-in">
            Password changed successfully! Logging out...
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedProfilePage;
